requestAnimationFrame(() => {
  themeBase.onDOMContentLoadedTrigger(() => {
    class TransparentHeader extends HTMLElement {
      constructor() {
        super();

        this.invertClass = 'header-section--style-inverted';
        this.wrapperTransparentClass = 'header-sections-wrapper--transparent';
        this.fullWidthLogoAttribute = 'data-transparent-full-width-logo';
        this.sectionSelector = '[data-invert-header-colors]';
        this.onScrollHandler = this.onScroll.bind(this);
        this.onResizeHandler = this.onResize.bind(this);
        this.onLazySectionLoadedHandler = this.onLazySectionLoaded.bind(this);
        this.onHeaderChangeHandler = this.onHeaderChange.bind(this);
        this.onSectionMutationHandler = this.onSectionMutation.bind(this);
        this.onObservedResizeHandler = this.onObservedResize.bind(this);
        this.onWindowLoadHandler = this.syncWhenConnected.bind(this);
        this.layoutOrderSignature = '';
        this.pendingHeaderRefresh = false;
        this.pendingObservedSync = false;
        this.hasManagedDesktopLayout = false;
        this.wasActive = false;
      }

      connectedCallback() {
        if (this.initialized) return;

        this.initialized = true;
        this.headerSection = this.querySelector('header-section');
        this.header = this.querySelector('.header');
        this.headerRoot = window.themeHeader?.getSectionRoot?.(this.headerSection) || this.headerSection?.closest?.('[id^="shopify-section-"]') || null;
        this.wrapper = window.themeHeader?.getWrapper?.() || document.querySelector('.header-sections-wrapper');
        this.sections = new Set();

        this.collectSections();
        this.sync();
        this.observeHeaderLayout();
        this.schedulePostLayoutSync();

        window.addEventListener('throttle30fpsScroll', this.onScrollHandler, {
          passive: true
        });
        window.addEventListener('throttle30fpsResize', this.onResizeHandler);
        window.addEventListener('LazySectionLoaded', this.onLazySectionLoadedHandler);
        window.addEventListener('HeaderPrimaryMaybeChanged', this.onHeaderChangeHandler);
        document.addEventListener('shopify:section:load', this.onSectionMutationHandler);
        document.addEventListener('shopify:section:unload', this.onSectionMutationHandler);
        document.addEventListener('shopify:section:reorder', this.onSectionMutationHandler);
      }

      disconnectedCallback() {
        window.removeEventListener('throttle30fpsScroll', this.onScrollHandler);
        window.removeEventListener('throttle30fpsResize', this.onResizeHandler);
        window.removeEventListener('LazySectionLoaded', this.onLazySectionLoadedHandler);
        window.removeEventListener('HeaderPrimaryMaybeChanged', this.onHeaderChangeHandler);
        document.removeEventListener('shopify:section:load', this.onSectionMutationHandler);
        document.removeEventListener('shopify:section:unload', this.onSectionMutationHandler);
        document.removeEventListener('shopify:section:reorder', this.onSectionMutationHandler);
        window.removeEventListener('load', this.onWindowLoadHandler);
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;

        if (this.headerSection) {
          this.headerSection.classList.remove(this.invertClass);
        }

        this.clearManagedFirstSectionInvert();
        this.clearDesktopStackOffsets();
        const layoutChanged = this.syncManagedDesktopLayout();
        this.clearSharedState();

        this.sections = new Set();
        this.initialized = false;

        if (layoutChanged) {
          this.scheduleHeaderRefresh();
        }
      }

      observeHeaderLayout() {
        if (!window.ResizeObserver) return;

        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(this.onObservedResizeHandler);

        [this.headerSection, this.header, this.headerRoot, this.wrapper]
        .filter(Boolean)
          .forEach((element) => this.resizeObserver.observe(element));
      }

      onObservedResize() {
        if (this.pendingObservedSync) return;

        this.pendingObservedSync = true;
        requestAnimationFrame(() => {
          this.pendingObservedSync = false;
          this.syncWhenConnected();
        });
      }

      syncWhenConnected() {
        if (!this.initialized || !this.isConnected) return;

        this.sync();
      }

      schedulePostLayoutSync() {
        requestAnimationFrame(() => requestAnimationFrame(() => this.syncWhenConnected()));

        if (window.scriptController?.onStyleReady) {
          window.scriptController.onStyleReady(() => this.syncWhenConnected(), ['base-start']);
        }

        if (document.readyState === 'complete') {
          this.syncWhenConnected();
        } else {
          window.addEventListener('load', this.onWindowLoadHandler, {
            once: true
          });
        }
      }

      getHeaderTrackingElement() {
        return this.headerSection?.querySelector('.sticky-header__content--sticky:not(.sticky-header__content--hidden)') ||
          this.header;
      }

      collectSections() {
        this.sections = new Set(document.querySelectorAll(this.sectionSelector));
      }

      onLazySectionLoaded(event) {
        if (!event.detail || !event.detail.element) return;

        const target = event.detail.element.querySelector(this.sectionSelector) ||
          (event.detail.element.matches(this.sectionSelector) ? event.detail.element : null);

        if (target) {
          this.sections.add(target);
          this.updateHeaderState();
        }
      }

      onScroll() {
        this.sync();
      }

      onResize() {
        this.sync();
      }

      onHeaderChange() {
        this.headerSection = this.querySelector('header-section');
        this.header = this.querySelector('.header');
        this.headerRoot = window.themeHeader?.getSectionRoot?.(this.headerSection) || this.headerSection?.closest?.('[id^="shopify-section-"]') || null;
        this.wrapper = window.themeHeader?.getWrapper?.() || document.querySelector('.header-sections-wrapper');
        this.observeHeaderLayout();
        this.sync();
      }

      onSectionMutation() {
        this.collectSections();
        this.onHeaderChange();
      }

      scheduleHeaderRefresh() {
        if (this.pendingHeaderRefresh) return;

        this.pendingHeaderRefresh = true;
        requestAnimationFrame(() => {
          this.pendingHeaderRefresh = false;
          window.dispatchEvent(new Event('HeaderPrimaryMaybeChanged'));
        });
      }

      getDesktopHeaderSection(child) {
        return child?.querySelector?.('header-section.header-section--desktop[data-header-layout]') || null;
      }

      canManageDesktopLayout() {
        return !!(
          this.wrapper &&
          this.headerRoot &&
          this.headerSection?.classList.contains('header-section--desktop')
        );
      }

      getWrapperChildren() {
        if (!this.wrapper) return [];

        const orderedChildren = window.themeHeader?.getOrderedWrapperChildren?.();
        if (orderedChildren?.length) return orderedChildren;

        return Array.from(this.wrapper.children).filter((child) => child.isConnected);
      }

      clearManagedDesktopLayoutAttributes() {
        if (!this.wrapper) return;

        Array.from(this.wrapper.children).forEach((child) => {
          child.style.removeProperty('order');
          child.removeAttribute(this.fullWidthLogoAttribute);
        });
      }

      syncManagedDesktopLayout() {
        if (!this.canManageDesktopLayout()) {
          if (!this.hasManagedDesktopLayout) return false;

          const changed = this.layoutOrderSignature !== '';
          this.clearManagedDesktopLayoutAttributes();
          this.layoutOrderSignature = '';
          this.hasManagedDesktopLayout = false;
          return changed;
        }

        const wrapperChildren = Array.from(this.wrapper.children).filter((child) => child.isConnected);
        const headerRootIndex = wrapperChildren.indexOf(this.headerRoot);

        if (headerRootIndex === -1) {
          const changed = this.layoutOrderSignature !== '';
          this.clearManagedDesktopLayoutAttributes();
          this.layoutOrderSignature = '';
          this.hasManagedDesktopLayout = false;
          return changed;
        }

        const childrenBeforeHeader = wrapperChildren.slice(0, headerRootIndex);
        const childrenAfterHeader = wrapperChildren.slice(headerRootIndex + 1);
        const reorderedDesktopHeaders = [];
        const trailingChildren = [];

        childrenAfterHeader.forEach((child) => {
          const desktopHeaderSection = this.getDesktopHeaderSection(child);
          if (desktopHeaderSection) {
            reorderedDesktopHeaders.push(child);
          } else {
            trailingChildren.push(child);
          }
        });

        const desiredOrder = [
          ...childrenBeforeHeader,
          ...reorderedDesktopHeaders,
          this.headerRoot,
          ...trailingChildren
        ];

        const nextSignature = desiredOrder.map((child, index) => `${child.id || child.className || index}:${index}`).join('|');
        const changed = nextSignature !== this.layoutOrderSignature;
        this.layoutOrderSignature = nextSignature;

        desiredOrder.forEach((child, index) => {
          child.style.order = `${index}`;
          if (child.classList.contains('full-width-logo-section-wrapper')) {
            const shouldOffsetFullWidthLogo = child !== this.headerRoot && this.headerSection?.classList.contains('header-section--transparent-index-page');
            child.toggleAttribute(this.fullWidthLogoAttribute, shouldOffsetFullWidthLogo);
          } else {
            child.removeAttribute(this.fullWidthLogoAttribute);
          }
        });

        this.hasManagedDesktopLayout = true;

        return changed;
      }

      isActive() {
        return window.themeHeader?.isActiveSection?.(this.headerSection) ?? (window.themeHeader?.isPrimarySection?.(this.headerSection) ?? true);
      }

      sync(updateSpacing = true) {
        const activeSection = window.themeHeader?.getActiveSection?.() || this.headerSection;
        const layoutChanged = this.syncManagedDesktopLayout();

        this.syncManagedFirstSectionInvert(activeSection);

        if (!this.isActive()) {
          this.headerSection?.classList.remove(this.invertClass);
          if (this.wasActive) {
            this.clearDesktopStackOffsets();
            this.clearSharedState(activeSection);
          }
          this.wasActive = false;

          if (layoutChanged) {
            this.scheduleHeaderRefresh();
          }
          return;
        }

        this.updateDesktopStackOffsets(activeSection);

        if (updateSpacing) {
          this.updateHeaderSpacing();
        }

        this.updateHeaderState();
        this.wrapper?.classList.add(this.wrapperTransparentClass);
        document.body.classList.add('body-transparent-header');
        this.wasActive = true;

        if (layoutChanged) {
          this.scheduleHeaderRefresh();
        }
      }

      syncManagedFirstSectionInvert(activeSection) {
        const firstMainSection = document.querySelector('main > .shopify-section');
        if (!firstMainSection) return;

        const shouldInvert = !!(activeSection && activeSection.classList.contains('header-section--first-section-inverted'));

        if (shouldInvert) {
          firstMainSection.setAttribute('data-invert-header-colors', '');
          firstMainSection.setAttribute('data-header-invert-managed', 'true');
          this.sections.add(firstMainSection);
          return;
        }

        this.clearManagedFirstSectionInvert(firstMainSection);
      }

      clearManagedFirstSectionInvert(firstMainSection = document.querySelector('main > .shopify-section')) {
        if (!firstMainSection || !firstMainSection.hasAttribute('data-header-invert-managed')) return;

        firstMainSection.removeAttribute('data-invert-header-colors');
        firstMainSection.removeAttribute('data-header-invert-managed');
        this.sections.delete(firstMainSection);
      }

      clearSharedState(activeSection = window.themeHeader?.getActiveSection?.() || null) {
        if (activeSection && activeSection !== this.headerSection && activeSection.closest('transparent-header')) {
          return;
        }

        this.wrapper?.classList.remove(this.wrapperTransparentClass);
        document.body.classList.remove('body-transparent-header');
        document.documentElement.style.setProperty('--transparent-header-spacing', '0px');
      }

      clearDesktopStackOffsets() {
        if (!this.wrapper) return;

        Array.from(this.wrapper.children).forEach((child) => {
          child.style.removeProperty('margin-top');
        });

        this.wrapper.querySelectorAll('header-section.header-section--transparent-index-page').forEach((section) => {
          section.style.removeProperty('top');
        });
      }

      updateDesktopStackOffsets(activeSection) {
        this.clearDesktopStackOffsets();

        if (!themeBase.isDesktop || !this.wrapper) return;
        if (!activeSection || activeSection !== this.headerSection) return;
        if (!this.headerSection?.classList.contains('header-section--transparent-index-page')) return;

        const wrapperChildren = this.getWrapperChildren();
        let flowHeight = 0;

        wrapperChildren.forEach((child) => {
          if (child === this.headerRoot) {
            this.headerSection.style.top = `${flowHeight}px`;
            return;
          }

          const childPosition = window.getComputedStyle(child).position;
          if (childPosition === 'absolute' || childPosition === 'fixed') return;

          const childHeight = themeBounds.get(child).height;
          flowHeight += childHeight;
        });
      }

      updateHeaderSpacing() {
        if (!this.header) return;

        const headerHeight = this.header.getBoundingClientRect().height;
        const headerTop = parseFloat(this.headerSection?.style?.top || '0') || 0;
        document.documentElement.style.setProperty('--transparent-header-spacing', `${headerTop + headerHeight}px`);
      }

      updateHeaderState() {
        const headerTrackingElement = this.getHeaderTrackingElement();
        if (!headerTrackingElement) return;

        const headerRect = headerTrackingElement.getBoundingClientRect();
        const requiredOverlap = headerRect.height * 0.5;

        if (!requiredOverlap || !this.sections.size) {
          this.headerSection.classList.remove(this.invertClass);
          return;
        }

        let shouldInvert = false;

        for (const section of this.sections) {
          if (!section || !section.isConnected) {
            this.sections.delete(section);
            continue;
          }

          const sectionRect = section.getBoundingClientRect();
          const overlapHeight = Math.min(headerRect.bottom, sectionRect.bottom) - Math.max(headerRect.top, sectionRect.top);

          if (overlapHeight > requiredOverlap) {
            shouldInvert = true;
            break;
          }
        }

        this.headerSection.classList.toggle(this.invertClass, shouldInvert);
      }
    }

    customElements.define('transparent-header', TransparentHeader);
  });
});